/**
 * Yatırım Fizibilite — Senaryo Listesi + Detay (R8, Faz F2)
 *
 * Tek bileşen, içinde liste↔detay geçişi (`useUrlState('senaryo','')`).
 * Anayasa uyumu:
 *  - Madde 1: Dropdown verileri (yatırım tipi/kategori) API'den beslenir, hardcode yok.
 *  - Madde 2/6: Senaryo soft delete default + pasiften aktifleştirme (M.6) akışı.
 *  - Madde 5: Backend MANAGE_FEASIBILITY enforce eder; frontend UX gating ek katman.
 *
 * Backend endpoint'leri (R8, canlı):
 *  - GET    /api/fizibilite/senaryolar?include_inactive=     (liste)
 *  - POST   /api/fizibilite/senaryolar                       (oluştur)
 *  - GET    /api/fizibilite/senaryolar/{id}                  (detay + kalemler + hesap)
 *  - PATCH  /api/fizibilite/senaryolar/{id}                  (varsayım düzenle / aktif=true)
 *  - DELETE /api/fizibilite/senaryolar/{id}[?hard=true]      (soft/hard)
 *  - POST   /api/fizibilite/senaryolar/{id}/kalemler         (yatırım kalemi ekle)
 *  - DELETE /api/fizibilite/senaryolar/{id}/kalemler/{kid}   (kalem sil)
 *  - GET    /api/fizibilite/yatirim-tipleri?include_inactive= (tip adı çözümleme + dropdown)
 *  - GET    /api/fizibilite/yatirim-kategorileri             (dropdown)
 *  - GET    /api/fizibilite/varsayilan-egri                  (önerilen başlangıç büyüme eğrisi)
 */

import React, { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import ErrorBanner from '../../components/ErrorBanner';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { colors, radius, styles } from '../../styles/theme';
import useUrlState from '../../hooks/useUrlState';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { formatTRY, formatPct, formatSayi } from '../../utils/analytics/format';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Stil yardımcıları ───────────────────────────────────────────
const inputStyle = {
  width: '100%',
  padding: '7px 9px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 13,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const labelStyle = { fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 };

const cardStyle = {
  background: colors.surface,
  borderRadius: radius.md,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: 20,
  marginBottom: 16,
};

const sectionTitleStyle = {
  margin: '0 0 14px 0',
  fontSize: 16,
  fontWeight: 700,
  color: colors.textPrimary,
};

const primaryBtn = {
  padding: '9px 16px',
  background: colors.primary,
  color: 'white',
  border: 'none',
  borderRadius: radius.sm,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

// Değerlendirme rozet renkleri — backend hesap.degerlendirme kodları.
const DEGERLENDIRME = {
  taslak: { label: 'Taslak', bg: '#e5e7eb', color: '#374151' },
  saglikli: { label: 'Sağlıklı', bg: '#d4edda', color: '#155724' },
  marjinal: { label: 'Marjinal', bg: '#fff3cd', color: '#856404' },
  riskli: { label: 'Riskli', bg: '#f8d7da', color: '#721c24' },
  karsiz: { label: 'Kârsız', bg: '#f8d7da', color: '#721c24' },
};

const DegerlendirmeBadge = ({ deger }) => {
  const meta = DEGERLENDIRME[deger] || { label: deger || '—', bg: '#eee', color: '#555' };
  return (
    <span
      style={{
        background: meta.bg,
        color: meta.color,
        padding: '3px 10px',
        borderRadius: radius.full,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
};

// ════════════════════════════════════════════════════════════════
//  LİSTE GÖRÜNÜMÜ
// ════════════════════════════════════════════════════════════════
const SenaryoListesi = ({ tipMap, tipListesi, canManage, onSelect }) => {
  const toast = useToast();
  const [senaryolar, setSenaryolar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Yeni senaryo inline form
  const [createOpen, setCreateOpen] = useState(false);
  const [yeniAd, setYeniAd] = useState('');
  const [yeniTipId, setYeniTipId] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/fizibilite/senaryolar${
        includeInactive ? '?include_inactive=true' : ''
      }`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('');
      const data = await res.json().catch(() => []);
      setSenaryolar(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      logError('Senaryolar yüklenemedi:', err);
      setError(err.message || 'Senaryolar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const submitCreate = async () => {
    if (!yeniAd.trim()) return toast.warning('Senaryo adı zorunludur.');
    setSaving(true);
    try {
      const payload = { ad: yeniAd.trim() };
      if (yeniTipId) payload.yatirim_tipi_id = parseInt(yeniTipId, 10);
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || '');
      toast.success('Senaryo oluşturuldu');
      setCreateOpen(false);
      setYeniAd('');
      setYeniTipId('');
      if (body.id != null) onSelect(String(body.id));
      else fetchList();
    } catch (err) {
      toast.error(err.message || 'Senaryo oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const aktiflestir = async (s) => {
    try {
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar/${s.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: true }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || '');
      }
      toast.success('Senaryo aktifleştirildi');
      fetchList();
    } catch (err) {
      toast.error(err.message || 'Aktifleştirilemedi');
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: colors.textSecondary,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Pasifleri göster
        </label>

        {canManage && !createOpen && (
          <button onClick={() => setCreateOpen(true)} style={primaryBtn}>
            + Yeni Senaryo
          </button>
        )}
      </div>

      {/* Yeni senaryo inline form */}
      {canManage && createOpen && (
        <div style={{ ...cardStyle, padding: 16 }}>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ flex: '2 1 220px' }}>
              <label style={labelStyle}>Senaryo Adı*</label>
              <input
                style={inputStyle}
                value={yeniAd}
                onChange={(e) => setYeniAd(e.target.value)}
                placeholder="Örn. Kadıköy Şube Açılışı"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCreate();
                }}
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Yatırım Tipi</label>
              <select
                style={inputStyle}
                value={yeniTipId}
                onChange={(e) => setYeniTipId(e.target.value)}
              >
                <option value="">— Seçiniz —</option>
                {tipListesi.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.ad}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={submitCreate}
                disabled={saving}
                style={{ ...primaryBtn, background: saving ? '#999' : styles.success }}
              >
                {saving ? 'Kaydediliyor...' : 'Oluştur'}
              </button>
              <button
                onClick={() => {
                  setCreateOpen(false);
                  setYeniAd('');
                  setYeniTipId('');
                }}
                disabled={saving}
                style={{ ...primaryBtn, background: '#6c757d' }}
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorBanner error={error} onRetry={fetchList} />

      <div
        style={{
          background: colors.surface,
          borderRadius: radius.md,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        {loading && senaryolar.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
            Yükleniyor...
          </div>
        ) : senaryolar.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💡</div>
            <div style={{ fontSize: 15, marginBottom: 4 }}>Henüz fizibilite senaryosu yok.</div>
            {canManage && (
              <div style={{ fontSize: 13 }}>
                <strong>+ Yeni Senaryo</strong> ile başlayın.
              </div>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Ad</th>
                <th style={{ padding: 10 }}>Yatırım Tipi</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Toplam Yatırım</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Yıllık ROI</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Geri Ödeme</th>
                <th style={{ padding: 10 }}>Değerlendirme</th>
                {canManage && <th style={{ padding: 10, textAlign: 'right' }}>İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {senaryolar.map((s) => {
                const isAktif = s.aktif !== false;
                const tip = s.yatirim_tipi_id != null ? tipMap.get(s.yatirim_tipi_id) : null;
                return (
                  <tr
                    key={s.id}
                    onClick={() => isAktif && onSelect(String(s.id))}
                    style={{
                      borderBottom: `1px solid ${colors.border}`,
                      opacity: isAktif ? 1 : 0.55,
                      background: isAktif ? 'white' : '#fafafa',
                      cursor: isAktif ? 'pointer' : 'default',
                    }}
                  >
                    <td style={{ padding: 10, fontWeight: 600 }}>{s.ad}</td>
                    <td style={{ padding: 10, color: '#666' }}>{tip ? tip.ad : '—'}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      {formatTRY(s.toplam_yatirim)}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      {s.roi_yillik_pct == null ? '—' : formatPct(s.roi_yillik_pct)}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      {s.geri_odeme_ay == null ? (
                        <span style={{ color: '#999' }}>ulaşmıyor</span>
                      ) : (
                        `${formatSayi(s.geri_odeme_ay, { decimals: 1, minDecimals: 0 })} ay`
                      )}
                    </td>
                    <td style={{ padding: 10 }}>
                      <DegerlendirmeBadge deger={s.degerlendirme} />
                    </td>
                    {canManage && (
                      <td
                        style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isAktif ? (
                          <button
                            onClick={() => onSelect(String(s.id))}
                            style={{
                              padding: '4px 12px',
                              background: '#0d6efd',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            Aç →
                          </button>
                        ) : (
                          <button
                            onClick={() => aktiflestir(s)}
                            style={{
                              padding: '4px 12px',
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            Aktifleştir
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  DETAY GÖRÜNÜMÜ
// ════════════════════════════════════════════════════════════════
const SonucKart = ({ baslik, deger, alt }) => (
  <div
    style={{
      background: colors.backgroundAlt,
      border: `1px solid ${colors.border}`,
      borderRadius: radius.md,
      padding: '14px 16px',
      flex: '1 1 160px',
      minWidth: 150,
    }}
  >
    <div style={{ fontSize: 11, color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {baslik}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: colors.textPrimary, marginTop: 4 }}>
      {deger}
    </div>
    {alt && <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>{alt}</div>}
  </div>
);

const SenaryoDetay = ({
  senaryoId,
  tipMap,
  tipListesi,
  kategoriMap,
  kategoriListesi,
  canManage,
  onBack,
}) => {
  const toast = useToast();
  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Varsayım form state (HAM string'ler — decimalInput için)
  const [form, setForm] = useState(null);
  // Büyüme eğrisi satırları: [{ ay: '1', doluluk: '60' }]
  const [egriRows, setEgriRows] = useState([]);
  // Yeni kalem form
  const [kalemKategoriId, setKalemKategoriId] = useState('');
  const [kalemAciklama, setKalemAciklama] = useState('');
  const [kalemTutar, setKalemTutar] = useState('');
  const [kalemSaving, setKalemSaving] = useState(false);

  const fetchDetay = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar/${senaryoId}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('');
      const data = await res.json();
      setDetay(data);
      setForm({
        ad: data.ad || '',
        aciklama: data.aciklama || '',
        yatirim_tipi_id: data.yatirim_tipi_id != null ? String(data.yatirim_tipi_id) : '',
        tahmini_aylik_hacim: data.tahmini_aylik_hacim == null ? '' : String(data.tahmini_aylik_hacim),
        tahmini_satis_fiyati: data.tahmini_satis_fiyati == null ? '' : String(data.tahmini_satis_fiyati),
        birim_degisken_maliyet:
          data.birim_degisken_maliyet == null ? '' : String(data.birim_degisken_maliyet),
        aylik_sabit_gider: data.aylik_sabit_gider == null ? '' : String(data.aylik_sabit_gider),
        varsayim_notu: data.varsayim_notu || '',
      });
      // buyume_egrisi obje → satırlar (ay-sırayla)
      const eg = data.buyume_egrisi && typeof data.buyume_egrisi === 'object' ? data.buyume_egrisi : null;
      if (eg) {
        const rows = Object.keys(eg)
          .map((k) => ({ ay: String(k), doluluk: String(eg[k]) }))
          .sort((a, b) => (parseInt(a.ay, 10) || 0) - (parseInt(b.ay, 10) || 0));
        setEgriRows(rows);
      } else {
        setEgriRows([]);
      }
      setError(null);
    } catch (err) {
      logError('Senaryo detayı yüklenemedi:', err);
      setError(err.message || 'Senaryo detayı yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [senaryoId]);

  useEffect(() => {
    fetchDetay();
  }, [fetchDetay]);

  // ─── Varsayılan (önerilen) büyüme eğrisini editöre yükle ───────
  // Tasarımın önerdiği başlangıç eğrisi backend'den gelir; otomatik KAYDETMEZ,
  // sadece satırları doldurur — kullanıcı yine "Kaydet" ile kalıcılaştırır.
  const varsayilanEgriYukle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fizibilite/varsayilan-egri`, {
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || '');
      const eg = body && typeof body.egri === 'object' && body.egri ? body.egri : null;
      if (!eg) throw new Error('Varsayılan eğri alınamadı');
      const rows = Object.keys(eg)
        .map((k) => ({ ay: String(k), doluluk: String(eg[k]) }))
        .sort((a, b) => (parseInt(a.ay, 10) || 0) - (parseInt(b.ay, 10) || 0));
      setEgriRows(rows);
      toast.success('Varsayılan eğri yüklendi');
    } catch (err) {
      toast.error(err.message || 'Varsayılan eğri yüklenemedi');
    }
  };

  // ─── Varsayım + büyüme eğrisi kaydet ───────────────────────────
  const buildEgriObject = () => {
    const valid = egriRows.filter((r) => r.ay !== '' && r.doluluk !== '');
    if (valid.length === 0) return null; // boş → null (tam kapasite)
    const obj = {};
    valid.forEach((r) => {
      const ay = parseInt(r.ay, 10);
      if (Number.isFinite(ay)) obj[String(ay)] = parseDecimal(r.doluluk, 0);
    });
    return Object.keys(obj).length > 0 ? obj : null;
  };

  const submitVarsayim = async () => {
    if (!form.ad.trim()) return toast.warning('Senaryo adı zorunludur.');
    setSaving(true);
    try {
      const payload = {
        ad: form.ad.trim(),
        aciklama: form.aciklama.trim() || null,
        yatirim_tipi_id: form.yatirim_tipi_id ? parseInt(form.yatirim_tipi_id, 10) : null,
        tahmini_aylik_hacim: form.tahmini_aylik_hacim === '' ? null : parseDecimal(form.tahmini_aylik_hacim),
        tahmini_satis_fiyati: form.tahmini_satis_fiyati === '' ? null : parseDecimal(form.tahmini_satis_fiyati),
        birim_degisken_maliyet:
          form.birim_degisken_maliyet === '' ? null : parseDecimal(form.birim_degisken_maliyet),
        aylik_sabit_gider: form.aylik_sabit_gider === '' ? null : parseDecimal(form.aylik_sabit_gider),
        varsayim_notu: form.varsayim_notu.trim() || null,
        buyume_egrisi: buildEgriObject(),
      };
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar/${senaryoId}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || '');
      toast.success('Varsayımlar kaydedildi');
      fetchDetay(); // sessiz refetch → sonuç kartları güncellenir
    } catch (err) {
      toast.error(err.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Yatırım kalemi ekle / sil ─────────────────────────────────
  const submitKalem = async () => {
    if (!kalemKategoriId) return toast.warning('Kategori seçiniz.');
    if (kalemTutar === '') return toast.warning('Tutar zorunludur.');
    setKalemSaving(true);
    try {
      const payload = {
        kategori_id: parseInt(kalemKategoriId, 10),
        aciklama: kalemAciklama.trim() || null,
        tutar: parseDecimal(kalemTutar),
      };
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar/${senaryoId}/kalemler`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || '');
      toast.success('Yatırım kalemi eklendi');
      setKalemKategoriId('');
      setKalemAciklama('');
      setKalemTutar('');
      fetchDetay();
    } catch (err) {
      toast.error(err.message || 'Kalem eklenemedi');
    } finally {
      setKalemSaving(false);
    }
  };

  const silKalem = async (kid) => {
    if (!window.confirm('Bu yatırım kalemi silinsin mi?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/fizibilite/senaryolar/${senaryoId}/kalemler/${kid}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || '');
      }
      toast.success('Kalem silindi');
      fetchDetay();
    } catch (err) {
      toast.error(err.message || 'Kalem silinemedi');
    }
  };

  // ─── Senaryoyu sil (soft) ──────────────────────────────────────
  const silSenaryo = async () => {
    if (!window.confirm('Bu senaryo pasifleştirilsin mi? (Pasifleri göster ile geri alınabilir)'))
      return;
    try {
      const res = await fetch(`${API_URL}/api/fizibilite/senaryolar/${senaryoId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || '');
      }
      toast.success('Senaryo pasifleştirildi');
      onBack();
    } catch (err) {
      toast.error(err.message || 'Silinemedi');
    }
  };

  if (loading && !detay) {
    return (
      <div>
        <button onClick={onBack} style={{ ...primaryBtn, background: '#6c757d', marginBottom: 16 }}>
          ← Geri
        </button>
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Yükleniyor...
        </div>
      </div>
    );
  }

  if (error && !detay) {
    return (
      <div>
        <button onClick={onBack} style={{ ...primaryBtn, background: '#6c757d', marginBottom: 16 }}>
          ← Geri
        </button>
        <ErrorBanner error={error} onRetry={fetchDetay} />
      </div>
    );
  }

  const hesap = detay?.hesap || {};
  const kalemler = detay?.kalemler || [];
  const projeksiyon = Array.isArray(hesap.aylik_projeksiyon) ? hesap.aylik_projeksiyon : [];
  const uyarilar = Array.isArray(hesap.uyarilar) ? hesap.uyarilar : [];

  const readOnly = !canManage;

  return (
    <div>
      {/* Üst bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <button onClick={onBack} style={{ ...primaryBtn, background: '#6c757d' }}>
          ← Geri
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <DegerlendirmeBadge deger={hesap.degerlendirme} />
          {canManage && (
            <button
              onClick={silSenaryo}
              style={{ ...primaryBtn, background: '#dc3545' }}
            >
              🗑️ Senaryoyu Sil
            </button>
          )}
        </div>
      </div>

      {/* ─── Sonuç kartları ─────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Sonuçlar</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <SonucKart baslik="Toplam Yatırım" deger={formatTRY(hesap.toplam_yatirim)} />
          <SonucKart
            baslik="Aylık Net Nakit"
            deger={formatTRY(hesap.tam_kapasite_aylik_net_nakit)}
            alt="tam kapasitede"
          />
          <SonucKart
            baslik="Geri Ödeme"
            deger={
              hesap.geri_odeme_ay == null
                ? '—'
                : `${formatSayi(hesap.geri_odeme_ay, { decimals: 1, minDecimals: 0 })} ay`
            }
            alt={hesap.geri_odeme_ay == null ? '24 ay içinde ulaşmıyor' : null}
          />
          <SonucKart
            baslik="Yıllık ROI"
            deger={hesap.roi_yillik_pct == null ? '—' : formatPct(hesap.roi_yillik_pct)}
          />
          <SonucKart
            baslik="Hacim Break-even"
            deger={
              hesap.hacim_break_even == null
                ? '—'
                : `${formatSayi(hesap.hacim_break_even, { decimals: 1, minDecimals: 0 })} adet/ay`
            }
          />
          <SonucKart
            baslik="Birim Katkı Payı"
            deger={hesap.birim_katki_payi == null ? '—' : formatTRY(hesap.birim_katki_payi, { decimals: 2 })}
          />
        </div>

        {uyarilar.length > 0 && (
          <div
            style={{
              marginTop: 16,
              background: colors.warningLight,
              border: `1px solid ${colors.warning}`,
              borderRadius: radius.sm,
              padding: '10px 14px',
              fontSize: 13,
              color: '#856404',
            }}
          >
            <strong>⚠️ Uyarılar</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
              {uyarilar.map((u, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {u}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ─── Varsayımlar formu ──────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Varsayımlar</h3>
        {form && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 14,
              }}
            >
              <div>
                <label style={labelStyle}>Senaryo Adı*</label>
                <input
                  style={inputStyle}
                  value={form.ad}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, ad: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Yatırım Tipi</label>
                <select
                  style={inputStyle}
                  value={form.yatirim_tipi_id}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, yatirim_tipi_id: e.target.value })}
                >
                  <option value="">— Seçiniz —</option>
                  {tipListesi.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.ad}
                    </option>
                  ))}
                  {/* Mevcut senaryo PASİF bir tipe referans veriyorsa: seçimi koru
                      (aktif listede yok → adı tipMap'ten çöz, "(pasif)" etiketiyle). */}
                  {form.yatirim_tipi_id !== '' &&
                    !tipListesi.some((t) => String(t.id) === String(form.yatirim_tipi_id)) && (
                      <option value={form.yatirim_tipi_id}>
                        {(tipMap.get(parseInt(form.yatirim_tipi_id, 10))?.ad || 'Bilinmeyen tip') +
                          ' (pasif)'}
                      </option>
                    )}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Tahmini Aylık Hacim (adet)</label>
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="decimal"
                  value={form.tahmini_aylik_hacim}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm({ ...form, tahmini_aylik_hacim: normalizeDecimalInput(e.target.value) })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={labelStyle}>Tahmini Satış Fiyatı (₺)</label>
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="decimal"
                  value={form.tahmini_satis_fiyati}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm({ ...form, tahmini_satis_fiyati: normalizeDecimalInput(e.target.value) })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={labelStyle}>Birim Değişken Maliyet (₺)</label>
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="decimal"
                  value={form.birim_degisken_maliyet}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm({ ...form, birim_degisken_maliyet: normalizeDecimalInput(e.target.value) })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={labelStyle}>Aylık Sabit Gider (₺)</label>
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="decimal"
                  value={form.aylik_sabit_gider}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm({ ...form, aylik_sabit_gider: normalizeDecimalInput(e.target.value) })
                  }
                  placeholder="0"
                />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Açıklama</label>
              <input
                style={inputStyle}
                value={form.aciklama}
                disabled={readOnly}
                onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
              />
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Varsayım Notu</label>
              <textarea
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                value={form.varsayim_notu}
                disabled={readOnly}
                onChange={(e) => setForm({ ...form, varsayim_notu: e.target.value })}
              />
            </div>

            {/* Büyüme eğrisi editörü */}
            <div style={{ marginTop: 18 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <label style={{ ...labelStyle, marginBottom: 0, fontWeight: 600 }}>
                  Büyüme Eğrisi (ay → doluluk %)
                </label>
                {canManage && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={varsayilanEgriYukle}
                      style={{ ...primaryBtn, padding: '5px 12px', fontSize: 12, background: '#6c757d' }}
                    >
                      Varsayılan eğriyi yükle
                    </button>
                    <button
                      onClick={() =>
                        setEgriRows([
                          ...egriRows,
                          { ay: String(egriRows.length + 1), doluluk: '' },
                        ])
                      }
                      style={{ ...primaryBtn, padding: '5px 12px', fontSize: 12, background: '#0d6efd' }}
                    >
                      + Satır
                    </button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: colors.textTertiary, marginBottom: 8 }}>
                Boş bırakılırsa tüm aylar tam kapasite (%100) kabul edilir.
              </div>
              {egriRows.length === 0 ? (
                <div style={{ fontSize: 12, color: '#999' }}>Eğri tanımlı değil (tam kapasite).</div>
              ) : (
                egriRows.map((r, idx) => (
                  <div
                    key={idx}
                    style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}
                  >
                    <input
                      style={{ ...inputStyle, width: 90 }}
                      type="text"
                      inputMode="numeric"
                      value={r.ay}
                      disabled={readOnly}
                      placeholder="Ay"
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setEgriRows(egriRows.map((x, i) => (i === idx ? { ...x, ay: v } : x)));
                      }}
                    />
                    <input
                      style={{ ...inputStyle, width: 120 }}
                      type="text"
                      inputMode="decimal"
                      value={r.doluluk}
                      disabled={readOnly}
                      placeholder="Doluluk %"
                      onChange={(e) => {
                        const v = normalizeDecimalInput(e.target.value);
                        setEgriRows(egriRows.map((x, i) => (i === idx ? { ...x, doluluk: v } : x)));
                      }}
                    />
                    <span style={{ fontSize: 12, color: colors.textSecondary }}>%</span>
                    {canManage && (
                      <button
                        onClick={() => setEgriRows(egriRows.filter((_, i) => i !== idx))}
                        style={{
                          padding: '4px 10px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Sil
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {canManage && (
              <div style={{ marginTop: 18, textAlign: 'right' }}>
                <button
                  onClick={submitVarsayim}
                  disabled={saving}
                  style={{ ...primaryBtn, background: saving ? '#999' : styles.success }}
                >
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Yatırım kalemleri ──────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Yatırım Kalemleri</h3>

        {kalemler.length === 0 ? (
          <div style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>
            Henüz yatırım kalemi eklenmemiş.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>Kategori</th>
                <th style={{ padding: 8 }}>Açıklama</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Tutar</th>
                {canManage && <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {kalemler.map((k) => (
                <tr key={k.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: 8 }}>
                    {k.kategori_ad || kategoriMap.get(k.kategori_id)?.ad || '—'}
                  </td>
                  <td style={{ padding: 8, color: '#666' }}>{k.aciklama || '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatTRY(k.tutar)}</td>
                  {canManage && (
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        onClick={() => silKalem(k.id)}
                        style={{
                          padding: '4px 10px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Sil
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              borderTop: `1px solid ${colors.border}`,
              paddingTop: 14,
            }}
          >
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Kategori*</label>
              <select
                style={inputStyle}
                value={kalemKategoriId}
                onChange={(e) => setKalemKategoriId(e.target.value)}
              >
                <option value="">— Seçiniz —</option>
                {kategoriListesi.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.ad}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: '2 1 200px' }}>
              <label style={labelStyle}>Açıklama</label>
              <input
                style={inputStyle}
                value={kalemAciklama}
                onChange={(e) => setKalemAciklama(e.target.value)}
                placeholder="Örn. Fırın ekipmanı"
              />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Tutar (₺)*</label>
              <input
                style={inputStyle}
                type="text"
                inputMode="decimal"
                value={kalemTutar}
                onChange={(e) => setKalemTutar(normalizeDecimalInput(e.target.value))}
                placeholder="0"
              />
            </div>
            <button
              onClick={submitKalem}
              disabled={kalemSaving}
              style={{ ...primaryBtn, background: kalemSaving ? '#999' : '#0d6efd' }}
            >
              {kalemSaving ? 'Ekleniyor...' : 'Ekle'}
            </button>
          </div>
        )}
      </div>

      {/* ─── Ay-ay projeksiyon ──────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>24 Aylık Projeksiyon</h3>
        {projeksiyon.length === 0 ? (
          <div style={{ fontSize: 13, color: '#999' }}>
            Projeksiyon için varsayımları (hacim, fiyat, maliyet) doldurun.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 6 }}>Ay</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>Doluluk</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>Net Nakit</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>Kümülatif</th>
                </tr>
              </thead>
              <tbody>
                {projeksiyon.map((p) => (
                  <tr
                    key={p.ay}
                    style={{
                      borderBottom: `1px solid ${colors.borderLight}`,
                      background: p.kumulatif >= 0 ? '#f6fff6' : 'white',
                    }}
                  >
                    <td style={{ padding: 6 }}>{p.ay}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>
                      {p.doluluk_pct == null ? '—' : formatPct(p.doluluk_pct)}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{formatTRY(p.net_nakit)}</td>
                    <td
                      style={{
                        padding: 6,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: p.kumulatif >= 0 ? colors.success : colors.danger,
                      }}
                    >
                      {formatTRY(p.kumulatif)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  KÖK BİLEŞEN
// ════════════════════════════════════════════════════════════════
const YatirimFizibilitePage = ({ embedded = false }) => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, PERMISSIONS.VIEW_FEASIBILITY, user?.effectivePermissions);
  const canManage = hasPermission(
    user?.role,
    PERMISSIONS.MANAGE_FEASIBILITY,
    user?.effectivePermissions,
  );

  const [seciliId, setSeciliId] = useUrlState('senaryo', '');

  // Dropdown verileri (Anayasa Madde 1 — API'den)
  const [tipMap, setTipMap] = useState(new Map());
  const [tipListesi, setTipListesi] = useState([]);
  const [kategoriMap, setKategoriMap] = useState(new Map());
  const [kategoriListesi, setKategoriListesi] = useState([]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    const loadDropdowns = async () => {
      try {
        const [tRes, kRes] = await Promise.all([
          // Tipler: pasifler de gelsin (include_inactive) — mevcut senaryoların
          // referans verdiği pasif tipin ADI yine çözümlenebilsin diye.
          fetch(`${API_URL}/api/fizibilite/yatirim-tipleri?include_inactive=true`, {
            headers: authHeaders(),
          }),
          fetch(`${API_URL}/api/fizibilite/yatirim-kategorileri`, { headers: authHeaders() }),
        ]);
        const tData = tRes.ok ? await tRes.json().catch(() => []) : [];
        const kData = kRes.ok ? await kRes.json().catch(() => []) : [];
        if (cancelled) return;
        // Tip map: TÜM tipler (pasif dahil) → mevcut senaryoların tipini çözebilmek için.
        const tm = new Map();
        (Array.isArray(tData) ? tData : []).forEach((t) => tm.set(t.id, t));
        setTipMap(tm);
        // Tip dropdown: yalnız aktif (Madde 1 — yeni seçimde pasif tip seçilemez)
        setTipListesi(
          (Array.isArray(tData) ? tData : []).filter((t) => t.aktif !== false),
        );
        // Kategori map: tümü (çözümleme için)
        const km = new Map();
        (Array.isArray(kData) ? kData : []).forEach((c) => km.set(c.id, c));
        setKategoriMap(km);
        // Kategori dropdown: yalnız aktif (Madde 1 — pasif seçilemez)
        setKategoriListesi(
          (Array.isArray(kData) ? kData : []).filter((c) => c.aktif !== false),
        );
      } catch (err) {
        logError('Fizibilite dropdownları yüklenemedi:', err);
      }
    };
    loadDropdowns();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const content = !canView ? (
    <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
      Bu sayfayı görüntüleme yetkiniz yok. (VIEW_FEASIBILITY gerekli)
    </div>
  ) : seciliId ? (
    <SenaryoDetay
      key={seciliId}
      senaryoId={seciliId}
      tipMap={tipMap}
      tipListesi={tipListesi}
      kategoriMap={kategoriMap}
      kategoriListesi={kategoriListesi}
      canManage={canManage}
      onBack={() => setSeciliId('')}
    />
  ) : (
    <SenaryoListesi
      tipMap={tipMap}
      tipListesi={tipListesi}
      canManage={canManage}
      onSelect={(id) => setSeciliId(id)}
    />
  );

  if (embedded) {
    return <div>{content}</div>;
  }

  return (
    <div>
      <Header title="Yatırım Fizibilite" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {content}
      </div>
    </div>
  );
};

export default YatirimFizibilitePage;
